import clsx from 'clsx';
import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import { useTranslation } from 'react-i18next';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { sqHomeScreenStore, sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { formatTime } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import { useDateTimeFormatPreferences } from '@/datetime/useDateTimeFormatPreferences.hook';
import { ITEM_TYPES, JOURNAL_OR_REPORT, UNNAMED_REPORT } from '@/homescreen/homescreen.constants';
import { isNewHomeScreenWithMeilisearchEnabled } from '@/services/systemConfiguration.utilities';
import {
  formatJournalReportContent,
  getItemIcon,
  getWorkbook,
  translateBreadcrumb,
  translateMeilisearchBreadcrumb,
} from '@/utilities/homescreen.utilities';
import { getLockingChannel } from '@/utilities/lock.constants';
import { subscribe } from '@/utilities/socket.utilities';
import { Icon } from '@seeqdev/qomponents';
import type { IconProps } from '@seeqdev/qomponents';

type Breadcrumb = {
  id: string;
  name: string;
  translationKey?: string;
};

interface NameCellProps {
  item: any;
  showBreadcrumbs: boolean;
  extraClassNames?: string;
  isCurrentWorkbook?: boolean;
}

export const NameCell = ({ item, showBreadcrumbs, extraClassNames, isCurrentWorkbook = false }: NameCellProps) => {
  const { t } = useTranslation();

  const [journalReportName, setJournalReportName] = useState<string>();
  const [journalReportContent, setJournalReportContent] = useState<string>();
  const isJournalReport = _.includes(JOURNAL_OR_REPORT, item.type);
  const searchText = useFluxPath(sqHomeScreenStore, (store) => store.searchParams.textSearch) as string | undefined;
  const isAllowedLanguageCharacter = (word: string): boolean => /^[^-_/.,')'\\p{Alnum} ]+/.test(word);
  const isFoundInSearchText = (word: string): boolean => {
    if (!searchText) {
      return false;
    }

    const needle = _.toLower(searchText);
    const haystack = _.toLower(word);
    return (_.includes(needle, haystack) && isAllowedLanguageCharacter(word)) || _.includes(haystack, needle);
  };

  useEffect(() => {
    if (isJournalReport) {
      getWorkbook(item.workbookId).then((workbook) => {
        const itemWorksheet = _.find(workbook?.worksheets, { worksheetId: item.worksheetId });
        if (itemWorksheet) {
          setJournalReportName(`${workbook.name} - ${itemWorksheet.name}`);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (item.name !== t('UNNAMED') && item.name !== UNNAMED_REPORT && isJournalReport) {
      formatJournalReportContent(searchText, item.id).then((content) => setJournalReportContent(content));
    }
  }, [searchText, item]);

  const renderBreadcrumbs = (crumbs: string[] | Breadcrumb[] = []) => (
    <span data-testid="itemBreadcrumb" className="xsmall">
      {isNewHomeScreenWithMeilisearchEnabled()
        ? (crumbs as string[]).map((crumb) => translateMeilisearchBreadcrumb(crumb)).join(' » ')
        : (crumbs as Breadcrumb[]).map((crumb) => translateBreadcrumb(crumb)).join(' » ')}
    </span>
  );

  const renderJournalReportSearchResults = (content: string) => (
    <span className="small">
      {_.map(_.split(content, /[, ]+/), (word, index) => {
        return (
          <span key={`${word}-${index}`} className={isFoundInSearchText(word) ? 'tw:font-bold' : ''}>
            <ContainerWithHTML content={`${word} `} />
          </span>
        );
      })}
    </span>
  );

  const [localLocked, setLocalLocked] = useState(false);
  useEffect(() => {
    let unsubscribe: () => void | undefined;
    if (item.type === SeeqNames.Types.Analysis && !item.locked) {
      unsubscribe = subscribe({
        channelId: getLockingChannel(item.id),
        onMessage: () => {
          setLocalLocked(true);
        },
      });
    }
    return () => {
      unsubscribe?.();
    };
  }, []);

  const renderNameWithLockedStatus = (item: any) => (
    <div className="flexColumnContainer">
      {item.name}
      {item.locked || localLocked ? (
        <Icon icon="fa-lock" extraClassNames="tw:m-[5px] tw:mt-[3px]" testId="lockedAnalysis" />
      ) : (
        <></>
      )}
    </div>
  );

  const getCrumbs = () => {
    if (isNewHomeScreenWithMeilisearchEnabled()) {
      return Array.isArray(item.breadcrumbPath) ? item.breadcrumbPath : [];
    }

    return Array.isArray(item.ancestors) ? item.ancestors : [];
  };

  return (
    <div className={clsx('flexRowContainer', extraClassNames)}>
      {item.type === ITEM_TYPES.CONTEXT_COMMENT ? (
        <>
          <span data-testid="itemName" className="xsmall">
            {item.name}
          </span>
          <span
            data-testid="itemDescription"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '400px',
              display: 'block',
            }}>
            {item.description}
          </span>
        </>
      ) : (
        <>
          {showBreadcrumbs && renderBreadcrumbs(getCrumbs())}
          <span data-testid="itemName">{journalReportName ?? renderNameWithLockedStatus(item)}</span>
          {isJournalReport && !_.isNil(journalReportContent) && (
            <span data-testid="journalReportPreview">{renderJournalReportSearchResults(journalReportContent)}</span>
          )}
        </>
      )}
      {isCurrentWorkbook && <span className="xsmall">({t('EXPLORER_MODAL.CURRENT_WORKBOOK')})</span>}
    </div>
  );
};

interface HomeScreenIconCellProps {
  item: any;
  spinning?: boolean;
}

export const HomeScreenItemIconCell = ({ item, spinning = false }: HomeScreenIconCellProps) => {
  const itemIcon = getItemIcon(item);

  return (
    <div data-testid={`icon_${item.id}`}>
      <IconWithSpinner
        spinning={spinning}
        icon={itemIcon}
        extraClassNames={clsx('workbookIcon', {
          workbenchDisplay: item.type !== ITEM_TYPES.CONTEXT_COMMENT,
          workbenchAnalysis: item.type === ITEM_TYPES.JOURNAL,
          workbenchReport: item.type === ITEM_TYPES.REPORT,
          actionBoard: item.type === ITEM_TYPES.ACTION_BOARD,
        })}
      />
    </div>
  );
};

interface DateCellProps {
  item: any;
  accessor: string;
}

export const DateCell = ({ item, accessor }: DateCellProps) => {
  const { use24HourFormat } = useDateTimeFormatPreferences();
  const timezone = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.timezone);

  if (!item[accessor]) {
    return null;
  }

  const format = getPreferredTimeFormat(use24HourFormat);
  return <span>{formatTime(item[accessor], timezone, format)}</span>;
};

export const ProgressCell = ({ percentage }: { percentage: number }) => (
  <div className="flexColumnContainer tw:items-center tw:justify-center">
    <span className="dataStatusIcon tw:align-middle">
      <CircularProgressbar value={percentage} text={_.toString(percentage)} />
    </span>
  </div>
);

export const IconCell = (props: IconProps) => (
  <div className="flexColumnContainer tw:items-center tw:justify-center">
    <Icon {...props} />
  </div>
);
